import { createStyles, makeStyles, Theme } from "@material-ui/core";

const usePixModalStyles = makeStyles((theme: Theme) =>
  createStyles({
    // Dialog
    dialogPaper: {
      borderRadius: 16,
      overflow: "hidden",
      maxWidth: 480,
      width: "100%",
      margin: 16,
      padding: 0,
      maxHeight: "90vh",
      display: "flex",
      flexDirection: "column" as const,
      [theme.breakpoints.down("xs")]: {
        margin: 8,
        maxHeight: "90dvh",
        borderRadius: 12,
      },
    },
    // Header
    header: {
      padding: "18px 20px",
      display: "flex",
      alignItems: "center",
      gap: 12,
      flexShrink: 0,
      borderBottom: "1px solid #F3F4F6",
    },
    headerPending: {
      background: "#fff",
    },
    headerConfirmed: {
      background: "#fff",
    },
    headerTitle: {
      flex: 1,
      minWidth: 0,
      fontSize: 18,
      fontWeight: 700,
      color: "#1F2937",
      margin: 0,
      lineHeight: 1.3,
    },
    badge: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "4px 12px",
      borderRadius: 20,
      fontSize: 12,
      fontWeight: 600,
      whiteSpace: "nowrap",
    },
    badgePending: {
      background: "#FEF3C7",
      color: "#B45309",
    },
    badgeConfirmed: {
      background: "#DCFCE7",
      color: "#16A34A",
    },
    badgeDot: {
      width: 7,
      height: 7,
      borderRadius: "50%",
      flexShrink: 0,
    },
    badgeDotPending: {
      background: "#F59E0B",
    },
    badgeDotConfirmed: {
      background: "#22C55E",
    },
    closeButton: {
      width: 28,
      height: 28,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      background: "transparent",
      color: "#9CA3AF",
      fontSize: 16,
      flexShrink: 0,
      "&:hover": {
        background: "#F3F4F6",
        color: "#6B7280",
      },
    },
    // Body
    body: {
      padding: 20,
      background: "#fff",
      overflowY: "auto" as const,
      overflowX: "hidden" as const,
      flex: 1,
      minHeight: 0,
    },
    // Order Card
    orderCard: {
      background: "#F9FAFB",
      border: "1px solid #E5E7EB",
      borderRadius: 10,
      padding: "14px 16px",
      marginBottom: 16,
    },
    orderCardConfirmed: {
      borderColor: "#DCFCE7",
    },
    orderCardTop: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 12,
      paddingBottom: 12,
      marginBottom: 12,
      borderBottom: "1px solid #E5E7EB",
    },
    orderNumber: {
      fontSize: 15,
      fontWeight: 700,
      color: "#1F2937",
      display: "flex",
      alignItems: "center",
      gap: 8,
      flexWrap: "wrap",
    },
    orderStatusBadge: {
      fontSize: 10,
      fontWeight: 700,
      padding: "2px 8px",
      borderRadius: 4,
      textTransform: "uppercase" as const,
      letterSpacing: 0.3,
    },
    orderStatusBlocked: {
      background: "#FEE2E2",
      color: "#DC2626",
    },
    orderStatusReleased: {
      background: "#DCFCE7",
      color: "#16A34A",
    },
    orderDelivery: {
      fontSize: 13,
      color: "#6B7280",
      whiteSpace: "nowrap" as const,
      flexShrink: 0,
    },
    orderDeliveryValue: {
      fontWeight: 700,
      color: "#1F2937",
    },
    orderDetailRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "baseline",
      gap: 16,
      padding: "3px 0",
    },
    orderDetailLabel: {
      fontSize: 13,
      color: "#6B7280",
      flexShrink: 0,
    },
    orderDetailValue: {
      fontSize: 13,
      fontWeight: 600,
      color: "#1F2937",
      textAlign: "right" as const,
      minWidth: 0,
    },
    orderDetailValueStrong: {
      fontWeight: 700,
    },
    // QR Code Section
    qrSection: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 16,
      marginBottom: 16,
      minWidth: 0,
      overflow: "hidden",
      [theme.breakpoints.down("xs")]: {
        gridTemplateColumns: "1fr",
      },
    },
    // Quando não há Pix copia e cola, só a mensagem de indisponível ocupa a
    // linha inteira (sem os campos ao lado).
    qrSectionSingle: {
      gridTemplateColumns: "1fr",
    },
    qrBox: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "center",
      minWidth: 0,
    },
    // QR Code indisponível (fallback quando não há copia e cola)
    qrUnavailable: {
      display: "flex",
      flexDirection: "column" as const,
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center" as const,
      gap: 10,
      padding: "18px 14px",
      border: "1px dashed #D1D5DB",
      borderRadius: 10,
      background: "#F9FAFB",
      minWidth: 0,
      width: "100%",
      boxSizing: "border-box" as const,
    },
    qrUnavailableIcon: {
      width: 54,
      height: 54,
      borderRadius: "50%",
      background: "#F3F4F6",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#9CA3AF",
      flexShrink: 0,
    },
    qrUnavailableTitle: {
      fontSize: 15,
      fontWeight: 700,
      color: "#1F2937",
      lineHeight: 1.3,
    },
    qrUnavailableText: {
      fontSize: 12,
      color: "#9CA3AF",
      lineHeight: 1.5,
      margin: 0,
    },
    qrHelperText: {
      fontSize: 12,
      fontStyle: "italic" as const,
      color: "#374151",
      lineHeight: 1.5,
      margin: "4px 0 16px",
    },
    // Value Section
    valueSection: {
      display: "flex",
      flexDirection: "column" as const,
      justifyContent: "flex-start",
      minWidth: 0,
    },
    valueLabel: {
      fontSize: 13,
      fontWeight: 400,
      color: "#9CA3AF",
      marginBottom: 2,
    },
    valueAmount: {
      fontSize: 30,
      fontWeight: 700,
      color: "#1F2937",
      lineHeight: 1.2,
      marginBottom: 4,
    },
    valueCurrency: {
      fontSize: 18,
      color: "#1F2937",
    },
    valueDecimals: {
      fontSize: 18,
    },
    valueSubtitle: {
      fontSize: 13,
      color: "#9CA3AF",
      marginBottom: 16,
    },
    // Copy row (campo copia e cola + botão Copiar)
    copyRow: {
      display: "flex",
      alignItems: "stretch",
      gap: 8,
      minWidth: 0,
    },
    copyField: {
      flex: 1,
      minWidth: 0,
      display: "block",
      border: "1px solid #E5E7EB",
      borderRadius: 8,
      background: "#fff",
      padding: "8px 12px",
      fontSize: 12,
      fontFamily: "monospace",
      color: "#6B7280",
      lineHeight: "20px",
      whiteSpace: "nowrap" as const,
      overflow: "hidden",
      textOverflow: "ellipsis" as const,
      boxSizing: "border-box" as const,
    },
    copyButton: {
      flexShrink: 0,
      padding: "8px 18px",
      border: "none",
      borderRadius: 8,
      background: "#D91F05",
      color: "#fff",
      fontSize: 13,
      fontWeight: 600,
      cursor: "pointer",
      fontFamily: "Montserrat, Arial, sans-serif",
      transition: "background 150ms ease",
      "&:hover": {
        background: "#B81904",
      },
    },
    // Confirmation Box
    confirmationBox: {
      background: "#F9FAFB",
      border: "1px solid #E5E7EB",
      borderRadius: 10,
      padding: "16px 18px",
      margin: "0 0 16px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
    },
    confirmInfo: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      minWidth: 0,
    },
    confirmIcon: {
      width: 34,
      height: 34,
      borderRadius: "50%",
      background: "#DCFCE7",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#16A34A",
      flexShrink: 0,
    },
    confirmTitle: {
      fontSize: 15,
      fontWeight: 700,
      color: "#1F2937",
      lineHeight: 1.2,
    },
    confirmText: {
      fontSize: 12,
      color: "#6B7280",
      marginTop: 2,
    },
    confirmAmount: {
      fontSize: 22,
      fontWeight: 700,
      color: "#1F2937",
      whiteSpace: "nowrap" as const,
      flexShrink: 0,
    },
    // Payment Details
    paymentDetails: {
      borderTop: "1px solid #E5E7EB",
      marginTop: 16,
      paddingTop: 16,
    },
    paymentRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 16,
      padding: "8px 0",
      borderBottom: "1px solid #F3F4F6",
    },
    paymentRowLabel: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontSize: 12,
      color: "#6B7280",
      fontWeight: 500,
      flexShrink: 0,
    },
    paymentRowValue: {
      fontSize: 12,
      fontWeight: 600,
      color: "#1F2937",
      minWidth: 0,
      textAlign: "right" as const,
      overflowWrap: "anywhere" as const,
      wordBreak: "break-word" as const,
    },
    paymentRowValueHighlight: {
      color: "#D91F05",
    },
    paymentRowValueHighlightGreen: {
      color: "#16A34A",
    },
    // Footer
    footer: {
      padding: "16px 20px",
      paddingBottom: 24,
      background: "#fff",
      borderTop: "1px solid #E5E7EB",
      display: "flex",
      gap: 12,
      flexShrink: 0,
      [theme.breakpoints.down("xs")]: {
        padding: "12px 16px",
        paddingBottom: "calc(20px + env(safe-area-inset-bottom, 16px))",
        gap: 8,
        flexWrap: "nowrap" as const,
      },
    },
    btnOutline: {
      marginLeft: "auto",
      padding: "8px 28px",
      borderRadius: 8,
      fontSize: 13,
      fontWeight: 600,
      cursor: "pointer",
      textAlign: "center" as const,
      background: "transparent",
      fontFamily: "Montserrat, Arial, sans-serif",
      transition: "background 150ms ease",
      whiteSpace: "nowrap" as const,
      flexShrink: 0,
      [theme.breakpoints.down("xs")]: {
        padding: "8px 20px",
        fontSize: 12,
      },
    },
    btnOutlineRed: {
      border: "2px solid #D91F05",
      color: "#D91F05",
      "&:hover": {
        background: "#FEE2E2",
      },
    },

    btnSecondary: {
      flex: 1,
      padding: 10,
      borderRadius: 8,
      fontSize: 13,
      fontWeight: 600,
      cursor: "pointer",
      textAlign: "center" as const,
      background: "#F9FAFB",
      border: "1px solid #E5E7EB",
      color: "#374151",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      fontFamily: "Montserrat, Arial, sans-serif",
      transition: "background 150ms ease",
      whiteSpace: "nowrap" as const,
      minWidth: 0,
      "&:hover": {
        background: "#F3F4F6",
      },
      "&:disabled": {
        opacity: 0.5,
        cursor: "not-allowed",
      },
      [theme.breakpoints.down("xs")]: {
        padding: "10px 8px",
        fontSize: 12,
        gap: 4,
      },
    },
    // Loading
    loadingContainer: {
      display: "flex",
      flexDirection: "column" as const,
      justifyContent: "center",
      alignItems: "center",
      minHeight: 200,
      width: "100%",
      position: "relative" as const,
      padding: "40px 20px",
      gap: 16,
    },
    loadingCloseButton: {
      position: "absolute" as const,
      top: 12,
      right: 12,
      width: 28,
      height: 28,
      borderRadius: "50%",
      border: "1px solid #E5E7EB",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      background: "#fff",
      color: "#6B7280",
      fontSize: 14,
      "&:hover": {
        background: "#F3F4F6",
      },
    },
    loadingText: {
      fontSize: 13,
      fontWeight: 500,
      color: "#6B7280",
      marginTop: 4,
    },
    // Summary header (consolidado: total + "X de Y pagos")
    summaryHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 12,
      paddingBottom: 16,
      marginBottom: 16,
      borderBottom: "1px solid #E5E7EB",
    },
    summaryTotal: {
      fontSize: 13,
      color: "#6B7280",
    },
    summaryTotalValue: {
      fontSize: 15,
      fontWeight: 700,
      color: "#1F2937",
    },
    summaryCounter: {
      display: "inline-block",
      fontSize: 13,
      fontWeight: 600,
      color: "#6B7280",
      whiteSpace: "nowrap" as const,
      flexShrink: 0,
    },
    summaryCounterAllPaid: {
      color: "#16A34A",
      fontWeight: 700,
    },
    // Pix tabs (Pix 1, Pix 2, …)
    tabsBar: {
      display: "flex",
      gap: 8,
      marginBottom: 16,
      flexWrap: "nowrap" as const,
      overflowX: "auto" as const,
      overflowY: "hidden" as const,
      paddingBottom: 6,
      scrollbarWidth: "thin" as const,
      WebkitOverflowScrolling: "touch" as const,
      "&::-webkit-scrollbar": {
        height: 6,
      },
      "&::-webkit-scrollbar-thumb": {
        background: "#D1D5DB",
        borderRadius: 3,
      },
      "&::-webkit-scrollbar-track": {
        background: "transparent",
      },
    },
    tab: {
      padding: "8px 14px",
      borderRadius: 8,
      boxSizing: "border-box" as const,
      border: "1px solid transparent",
      background: "#F3F4F6",
      cursor: "pointer",
      flexShrink: 0,
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      fontFamily: "Montserrat, Arial, sans-serif",
      fontSize: 13,
      fontWeight: 600,
      color: "#6B7280",
      whiteSpace: "nowrap" as const,
      transition: "all 150ms ease",
      "&:hover": {
        background: "#E9EBEF",
      },
    },
    tabLabel: {
      whiteSpace: "nowrap" as const,
    },
    tabActive: {
      background: "#fff",
      borderColor: "#E5E7EB",
      color: "#111827",
      boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
      "&:hover": {
        background: "#fff",
      },
    },
    tabInactive: {},
    tabStatusDot: {
      width: 8,
      height: 8,
      borderRadius: "50%",
      flexShrink: 0,
    },
    tabStatusDotPaid: {
      background: "#22C55E",
    },
    tabStatusDotPending: {
      background: "#F59E0B",
    },
    tabValue: {
      fontSize: 12,
      fontWeight: 500,
      color: "#6B7280",
      whiteSpace: "nowrap" as const,
      lineHeight: 1.2,
    },
  })
);

export default usePixModalStyles;
